<template>
  <div v-loading.fullscreen="loading" v-show="!loading" class="scan-view">
    <div class="barcode-scanner">
      <div id="scan-area">
        <div class="scan-selection-camera" @click="openDialog" v-show="cameras.length > 0">
          <IconSwitch />
        </div>
        <div id="video-area">
          <video playsinline autoplay id="video"></video>
        </div>
      </div>

      <BarcodeSymbologiesSelection class="absolute bottom-2 z-50 left-8 flex w-52"
        @update:onSymbologiesChange="setBarcodeSymbologies" v-model:symbologies="symbologies" />

      <div v-if="showMultipleScan" class="absolute z-50 right-3 flex top flex-col justify-center items-center bottom-2">
        <BulkScanSwitchButton @update:isMultipleScan="isMultipleScan = $event" :isMultipleScan="isMultipleScan" />
      </div>
    </div>

    <div>
      <div :class="['dectect-input', isScanditReader ? 'margin-ocr' : 'margin-top-reader']">
        <el-input :readonly="isReadOnlyInput" class="text-center text-lg" v-model="valueCode"> </el-input>
      </div>
      <div class="step-button">
        <el-button :disabled="!valueCode" class="clear-button el-button--primary is-circle is-larger"
          @click="resetScannedValue(true)">
          {{ $t('clear') }}
        </el-button>
        <el-button class="el-button--primary is-circle is-larger" @click="confirm">
          {{ $t('ok') }}
        </el-button>
      </div>
    </div>
  </div>
  <SelectionCameraPopup v-model:selectedCamera="selectedCamera" :cameras="cameras" v-model:isShow="isOpenDialog" />
</template>
<script lang="ts">
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import BarcodeReaderMixin from '@/components/mixins/BarcodeReaderMixin.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import { BARCODE_SCAN } from '@/utils/constants'
import { EBCRType } from '@/utils/enums'
import errorHandler from '@/utils/errorHandler'
import { openMessage, promptVideoPermission } from '@/utils/helpers'
import { IProject } from '@/utils/types/projectTypes'
import { BrowserMultiFormatReader, Result } from '@zxing/library'
import 'cropperjs/dist/cropper.css'
import { Barcode, BarcodeCapture } from 'scandit-web-datacapture-barcode'
import { mixins, Options } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'
import BulkScanSwitchButton from './common/BulkScanSwitchButton.vue'

// Mixins
@Options({
  components: {
    BarcodeSymbologiesSelection,
    BulkScanSwitchButton,
    IconSwitch,
    SelectionCameraPopup,
  },
  emits: ['update:modelValue', 'update:confirm'],
  name: 'ScanComponent',
})
export default class OCRComponent extends mixins(BarcodeReaderMixin) {
  @Prop({ type: String }) readonly fieldName!: string
  @Prop({ default: EBCRType.DEFAULT }) customBarcodECameraReaderType!: EBCRType // This will override customBarcodECameraReaderType from mixins
  @Prop({ type: Boolean }) readonly isShowMultipleScanData?: boolean
  @Prop({ type: [String, Number] }) readonly initialValue?: string | number
  @Prop({ type: Boolean, default: false }) readonly isReadOnlyInput?: boolean
  codeReader: BrowserMultiFormatReader | null = new BrowserMultiFormatReader(
    undefined,
    BARCODE_SCAN.timeBetweenScansMillis as number
  )

  _barcodeDataCapture: BarcodeCapture | undefined
  loading = true
  video: HTMLVideoElement | null = null
  pictureWidth = 320
  pictureHeight = 320
  isCaptured = false

  confirm() {
    this.$emit('update:confirm', this.valueCode.trim())
  }

  defaultScanReader(result: Result, codeReader: BrowserMultiFormatReader) {
    this.codeReader = codeReader
    const tempResult: { [key: string]: unknown } = { ...result }
    if (!tempResult?.text) {
      return
    }

    if (this.showMultipleScan && this.isMultipleScan) {
      this.handleMultipleScan((tempResult.text as string) || '')
      return
    }
    this.handleScanResult((tempResult.text as string) || '')
  }

  scanditDataResult(symbology: Barcode) {
    if (this.showMultipleScan && this.isMultipleScan) {
      this.handleMultipleScan((symbology?.data as string) || '')
      return
    }
    if (!symbology) return
    this.handleScanResult((symbology?.data as string) || '')
  }

  async barcodeDetectorPolyFillScanReader(result: DetectedBarcode[]) {
    if (result.length < 1) return
    const firstDetectedBarcode = result[0]?.rawValue || ''

    if (this.showMultipleScan && this.isMultipleScan) {
      this.handleMultipleScan(firstDetectedBarcode)
      return
    }
    this.handleScanResult(firstDetectedBarcode)
  }

  handleScanResult(plainText: string) {
    openMessage(this.$t('work_center_scanned_value'), 'success')
    this.valueCode = plainText
  }

  get barcode() {
    return this.$store.state.barcode.barcode
  }

  get project(): IProject {
    return this.$store.state.project.details
  }

  async mounted() {
    // Set initial value if provided
    if (this.initialValue) {
      this.valueCode = this.initialValue.toString()
    }

    this.showMultipleScan = this.isShowMultipleScanData ?? false

    await this.initCameraSelection()
    if (this.isCustomVideoReader) {
      await promptVideoPermission()
    }

    await this.setupCameraSetting()
  }

  get isCustomVideoReader() {
    return !(this.customBarcodECameraReaderType === EBCRType.SCANDIT)
  }

  async setupCameraSetting() {
    switch (this.barcodeReaderType as EBCRType) {
      case EBCRType.SCANDIT:
        await this.initScanditReader()
        break
      case EBCRType.BARCODE_DETECTION:
        await this.initBarcodeReaderPolyFill()
        break
      default:
        await promptVideoPermission()
        await this.initZxingReader()
        break
    }
  }

  get isScanditReader() {
    return this.customBarcodECameraReaderType === EBCRType.SCANDIT
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }
    if (this.selectedCamera) {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            width: { ideal: 1000 },
            height: { ideal: 1000 },
            frameRate: {
              ideal: 30,
              max: 60,
            },
            aspectRatio: 1,
            deviceId: { exact: this.selectedCamera },
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          console.log('error', e)
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    } else {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: 'environment',
            aspectRatio: 1,
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    }
  }

  async handleMultipleScan(plainText: string) {
    if (this.isHandleMultipleScan) {
      return
    }

    const contentDialog = this.getContentDialog(plainText)
    this.isHandleMultipleScan = true

    this.$confirm(contentDialog, this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
      closeOnClickModal: true,
      showCancelButton: true,
      dangerouslyUseHTMLString: true,
    })
      .then(() => {
        this.valueCode = this.processValueCode(plainText)
      })
      .finally(() => {
        this.isHandleMultipleScan = false
      })
  }

  processValueCode(value: string) {
    let result = ''
    if (this.valueCode.trim() !== '') {
      result = `${this.valueCode.trim()} ${value}`
    } else {
      result = value
    }
    return result.trim()
  }

  resetScannedValue(isShowConfirmation = false) {
    if (!isShowConfirmation) {
      this.valueCode = ''
      return
    }
    this.$confirm(this.$t('confirm_reset_scanned_value'), this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      cancelButtonText: this.$t('cancel'),
      type: 'warning',
    }).then(() => {
      this.valueCode = ''
    })
  }
}
</script>
<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';

.margin-ocr {
  margin-top: 10px;
}

.margin-top-reader {
  margin-top: 16px;
}

.step-button {
  display: flex;
  justify-content: center;
  padding-top: 10px;
}

.media-container {
  width: 100%;
  position: relative;
  height: 40vh;

  video {
    top: 0;
    left: 0;
    width: 100%;
    object-fit: cover;
    height: 40vh;
  }
}

.media-container:deep() {
  &.scandit-container {
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
  }
}

.dectect-input {
  padding: 0 30px 0 30px;
  text-align-last: center;
}

.scan-view {
  text-align: center;
}

.message-wrapper {
  margin-top: 10px;
  word-break: break-word;
  position: absolute;
  z-index: 100;
  color: $pure-white;
  text-align: center;
  padding: 0 2rem;
  width: 100%;
  box-sizing: border-box;
  font-size: 0.85rem;

  &.top {
    margin-top: 10px;

    >div {
      padding: 8px 12px;
      background: rgb(0, 0, 0, 0.7);
      border-radius: 5px;
    }
  }
}

.image-container {
  max-width: 100%;
  height: 40vh;
  display: none;
  object-fit: cover;
}

.img {
  max-width: 100%;
  height: 40vh;
}

.barcode-scanner {
  height: 60vh;
  background: rgb(32, 32, 32);
  position: relative;
}

.barcode-scanner video {
  width: 100%;
  height: 100%;
}
</style>
